import React, { useState, useMemo } from 'react';
import CaseHistoryItem from './CaseHistoryItem';
import './CaseHistoryPanel.css';

/**
 * Filter options for case history
 */
const FILTER_OPTIONS = {
  ALL: 'all',
  TRIGGERED: 'triggered',
  SAVED: 'saved',
};

/**
 * CaseHistoryDrawer - Attached drawer showing saved case history
 *
 * Features:
 * - Search by patient name
 * - Filter by status (All/Triggered/Saved)
 * - Select all / individual selection
 * - Edit, delete, re-trigger actions
 * - Loading states for operations
 */
const CaseHistoryPanel = ({
  // Panel control
  isOpen,
  onClose,

  // Data
  cases = [],
  selectedIds = [],
  editingCaseKey = null,
  planName = '',

  // Loading state
  isLoading = false,

  // Selection handlers
  onSelectCase,
  onSelectAll,
  onDeselectAll,

  // Action handlers
  onEditCase,
  onDeleteCase,
  onRetriggerSelected,
  onDeleteSelected,
  onClearEditing,
}) => {
  // Local state for search and filter
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(FILTER_OPTIONS.ALL);

  // Filter and search cases
  const filteredCases = useMemo(() => {
    let result = cases;

    // Apply status filter
    if (activeFilter === FILTER_OPTIONS.TRIGGERED) {
      result = result.filter((c) => c.wasTriggered);
    } else if (activeFilter === FILTER_OPTIONS.SAVED) {
      result = result.filter((c) => !c.wasTriggered);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((c) => {
        const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
        return fullName.includes(query);
      });
    }

    return result;
  }, [cases, activeFilter, searchQuery]);

  // Selection state
  const allSelected = filteredCases.length > 0 && selectedIds.length === filteredCases.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < filteredCases.length;

  // Counts for filter badges
  const triggeredCount = cases.filter((c) => c.wasTriggered).length;
  const savedCount = cases.filter((c) => !c.wasTriggered).length;

  const handleSelectAllChange = () => {
    if (allSelected) {
      onDeselectAll();
    } else {
      onSelectAll();
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className={`case-creator-attached-drawer ${isOpen ? 'open' : ''}`}>
      <div className="case-history-drawer">
        {/* Header */}
        <div className="case-history-drawer__header">
          <h4 className="case-history-drawer__title">
            <span className="case-history-drawer__title-icon">
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 4V10L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </span>
            Saved Cases
            <span className="case-history-drawer__count">{cases.length}</span>
          </h4>
          <button
            type="button"
            className="case-history-drawer__close"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Search Bar */}
        {cases.length > 0 && (
          <div className="case-history-drawer__search">
            <div className="case-history-drawer__search-input-wrapper">
              <svg
                className="case-history-drawer__search-icon"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                type="text"
                className="case-history-drawer__search-input"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={handleSearchChange}
                aria-label="Search cases"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="case-history-drawer__search-clear"
                  onClick={clearSearch}
                  aria-label="Clear search"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Filter Pills */}
        {cases.length > 0 && (
          <div className="case-history-drawer__filters">
            <button
              type="button"
              className={`case-history-drawer__filter-pill ${activeFilter === FILTER_OPTIONS.ALL ? 'active' : ''}`}
              onClick={() => setActiveFilter(FILTER_OPTIONS.ALL)}
            >
              All
              <span className="case-history-drawer__filter-count">{cases.length}</span>
            </button>
            <button
              type="button"
              className={`case-history-drawer__filter-pill ${activeFilter === FILTER_OPTIONS.TRIGGERED ? 'active' : ''}`}
              onClick={() => setActiveFilter(FILTER_OPTIONS.TRIGGERED)}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              Triggered
              <span className="case-history-drawer__filter-count">{triggeredCount}</span>
            </button>
            <button
              type="button"
              className={`case-history-drawer__filter-pill ${activeFilter === FILTER_OPTIONS.SAVED ? 'active' : ''}`}
              onClick={() => setActiveFilter(FILTER_OPTIONS.SAVED)}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
              </svg>
              Saved
              <span className="case-history-drawer__filter-count">{savedCount}</span>
            </button>
          </div>
        )}

        {/* Editing Banner */}
        {editingCaseKey && (
          <div className="case-history-drawer__editing-banner">
            <span className="case-history-drawer__editing-text">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Editing
            </span>
            <button
              type="button"
              className="case-history-drawer__editing-clear"
              onClick={onClearEditing}
              aria-label="Clear editing selection"
            >
              Clear
            </button>
          </div>
        )}

        {/* Toolbar with Select All */}
        {filteredCases.length > 0 && (
          <div className="case-history-drawer__toolbar">
            <label className="case-history-drawer__select-all">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={handleSelectAllChange}
              />
              <span>{allSelected ? 'Deselect all' : 'Select all'}</span>
            </label>
            {searchQuery && (
              <span className="case-history-drawer__result-count">
                {filteredCases.length} result{filteredCases.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* Case List */}
        <div className="case-history-drawer__list">
          {isLoading ? (
            // Loading skeleton
            <div className="case-history-drawer__loading">
              {[1, 2, 3].map((i) => (
                <div key={i} className="case-history-item--skeleton">
                  <div className="skeleton skeleton--checkbox" />
                  <div className="skeleton--content">
                    <div className="skeleton skeleton--name" />
                    <div className="skeleton skeleton--meta" />
                  </div>
                </div>
              ))}
            </div>
          ) : cases.length === 0 ? (
            <div className="case-history-drawer__empty">
              <svg className="case-history-drawer__empty-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span className="case-history-drawer__empty-text">No saved cases for this plan</span>
              <span className="case-history-drawer__empty-hint">Cases you trigger or save will appear here</span>
            </div>
          ) : filteredCases.length === 0 ? (
            <div className="case-history-drawer__empty">
              <svg className="case-history-drawer__empty-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span className="case-history-drawer__empty-text">No matching cases</span>
              <span className="case-history-drawer__empty-hint">Try a different search or filter</span>
            </div>
          ) : (
            filteredCases.map((caseData, index) => (
              <CaseHistoryItem
                key={caseData.key}
                caseData={caseData}
                selected={selectedIds.includes(caseData.key)}
                isEditing={editingCaseKey === caseData.key}
                onSelect={onSelectCase}
                onEdit={onEditCase}
                onDelete={onDeleteCase}
                animationDelay={index * 0.03}
              />
            ))
          )}
        </div>

        {/* Footer Actions */}
        <div className="case-history-drawer__footer">
          <div className="case-history-drawer__selection-info">
            Selected: <span className="case-history-drawer__selection-count">{selectedIds.length}</span>
          </div>
          <div className="case-history-drawer__actions">
            <button
              type="button"
              className="case-history-drawer__btn case-history-drawer__btn--primary"
              disabled={selectedIds.length === 0 || isLoading}
              onClick={onRetriggerSelected}
            >
              {isLoading ? (
                <span className="case-history-drawer__btn-spinner" />
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M23 4v6h-6M1 20v-6h6"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
              )}
              Re-trigger
            </button>
            <button
              type="button"
              className="case-history-drawer__btn case-history-drawer__btn--danger"
              disabled={selectedIds.length === 0 || isLoading}
              onClick={onDeleteSelected}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseHistoryPanel;
